import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  makeStyles,
  Button,
  ConfirmationDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputField,
  LinearProgress,
  Typography,
  Tooltip,
  Hidden,
  CheckIcon
} from '@paintscout/ui'
import type { MuiTheme as Theme, DialogProps, StyleClasses } from '@paintscout/ui'
import { DateRangePicker, DropdownSelect, useClientOptions, useDialogs, useUsers } from '@paintscout/ui-business'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import ClearIcon from '@material-ui/icons/Clear'
import type { CalendarEvent } from 'paintscout'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import { getCrews, formatName } from '@paintscout/util/builder'
import MemberSelection from 'src/components/pages/scheduling/PainterScheduleView/ScheduleJobDialog/MemberSelection'
import { useScheduleJobDialogStyles } from 'src/components/pages/scheduling/PainterScheduleView/ScheduleJobDialog/useScheduleJobDialogStyles'
import type {
  OpenPanel,
  ScheduleJobMember
} from 'src/components/pages/scheduling/PainterScheduleView/ScheduleJobDialog/types'
import moment from 'moment'
import isEqual from 'lodash/isEqual'
import { removeAuth0Prefix } from '@paintscout/util/users'
import { escapeHref } from '@paintscout/util/util'
import { getCrewPalette, getProjectTypeLabel } from '../../common/ProductionCalendar/util'
import type { CrewPalette } from '../../common/ProductionCalendar/util'

interface StyleProps extends ProductionEventDialogProps {
  crewPalette: CrewPalette
}

const useStyles = makeStyles<Theme, StyleProps>(
  (theme) => ({
    root: {
      overflow: 'visible',
      [theme.breakpoints.down('xs')]: {
        overflow: 'hidden'
      }
    },
    dialogTitle: {},
    dialogContent: {
      overflow: 'visible',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        overflow: 'auto'
      }
    },
    cancel: {},
    confirm: {},
    infoDiv: {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
      padding: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1)
    },
    infoDivTextContainer: {
      display: 'flex',
      gap: theme.spacing(1)
    },
    datePicker: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      [theme.breakpoints.down('md')]: {
        alignItems: 'flex-end'
      }
    },
    datePickerIconButton: {
      border: `1px solid ${theme.paintscout.palette.primary}`,
      borderRadius: theme.shape.borderRadius,
      color: theme.paintscout.palette.primary,
      marginBottom: `-${theme.spacing(2.5)}px`,
      height: '40px',
      width: '40px',
      '&:hover': {
        backgroundColor: theme.paintscout.palette.primary,
        color: theme.palette.common.white
      }
    },
    linearProgress: {
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(1),
      height: theme.spacing(2),
      borderRadius: '5px',
      backgroundColor: ({ crewPalette }) => crewPalette.track
    },
    linearProgressBar: {
      borderRadius: '5px',
      backgroundColor: ({ crewPalette }) => crewPalette.progressFill
    },
    crewOption: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1)
    },
    crewOptionBar: {
      width: 5,
      minHeight: 32,
      alignSelf: 'stretch',
      borderRadius: 4,
      flexShrink: 0
    },
    crewOptionText: {
      display: 'flex',
      flexDirection: 'column'
    },
    crewOptionSubtext: {
      fontSize: theme.typography.pxToRem(12),
      color: '#a2afc3'
    }
  }),
  { name: 'ProductionEventDialog' }
)

export interface ProductionEventDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  event: CalendarEvent
  isNew?: boolean
  hideCalendarLink?: boolean
  onCancelEvent: (event: CalendarEvent) => any
  onConfirm: (event: CalendarEvent) => any
}

function ProductionEventDialog(props: ProductionEventDialogProps) {
  const { onConfirm, onCancelEvent, event, isNew, hideCalendarLink, ...baseDialogProps } = props
  const { enqueueSnackbar } = useSnackbar()
  const [isLoading, setIsLoading] = useState(false)
  const { users } = useUsers()
  const { options } = useClientOptions()

  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()

  const activeCrews = getCrews({ options })
  const allCrews = getCrews({ options, includeTrashed: true })
  const crews = allCrews
  const [currentEvent, setCurrentEvent] = useState<CalendarEvent>(event)
  const [initialEvent] = useState<CalendarEvent>(event)

  const activeCrewOptions = useMemo(
    () =>
      activeCrews.map((crew) => ({
        value: crew._id,
        label: crew.name,
        crewColor: crew.color,
        projectManagerName: crew.projectManager
          ? `${crew.projectManager.firstName} ${crew.projectManager.lastName}`
          : 'No Lead'
      })),
    [activeCrews]
  )

  const existingCrew = event.crew ? allCrews.find((c) => c._id === event.crew) : null
  const crewOptions = useMemo(
    () =>
      existingCrew?.trashed
        ? [
            {
              value: existingCrew._id,
              label: `${existingCrew.name} (Deleted)`,
              crewColor: existingCrew.color,
              projectManagerName: existingCrew.projectManager
                ? `${existingCrew.projectManager.firstName} ${existingCrew.projectManager.lastName}`
                : 'No Lead',
              disabled: true
            },
            ...activeCrewOptions
          ]
        : activeCrewOptions,
    [activeCrewOptions, existingCrew]
  )

  const [currentCrew, setCurrentCrew] = useState(crewOptions.find((c) => c.value === event.crew) || null)
  const crewPalette = getCrewPalette(currentCrew?.crewColor)
  const classes = useStyles({ ...props, crewPalette })
  const scheduleClasses = useScheduleJobDialogStyles()
  const [openPanel, setOpenPanel] = useState<OpenPanel>('none')

  const members: ScheduleJobMember[] = useMemo(() => {
    const crewMembers = activeCrews.flatMap((crew) =>
      [crew.projectManager, crew.siteLead, ...(crew.members ?? [])]
        .filter((m): m is NonNullable<typeof m> => !!m?.id)
        .map((m) => ({
          id: m.id,
          name: formatName(m),
          crewId: crew._id,
          crewName: crew.name,
          crewColor: crew.color
        }))
    )
    const crewMemberIds = new Set(crewMembers.map((m) => removeAuth0Prefix(m.id)))
    const unassigned: ScheduleJobMember[] = (users ?? [])
      .filter((u) => u.user_id && !crewMemberIds.has(removeAuth0Prefix(u.user_id)))
      .map((u) => ({
        id: u.user_id,
        name: [u.user_metadata?.firstName, u.user_metadata?.lastName].filter(Boolean).join(' ') || u.user_id
      }))
    return [...crewMembers, ...unassigned]
  }, [activeCrews, users])

  const getCrewMemberIds = useMemo(
    () => (crewId: string | null) => {
      if (!crewId) return []
      return members.filter((m) => m.crewId === crewId).map((m) => m.id)
    },
    [members]
  )

  const initialMemberIds = useMemo(() => {
    const assignedUserEntries = event.assignedUsers
      ? Object.entries(event.assignedUsers).filter(([, v]) => v.assigned)
      : []

    if (assignedUserEntries.length === 0) {
      return new Set(getCrewMemberIds(event.crew ?? null))
    }
    const assignedIds = new Set(assignedUserEntries.map(([id]) => id))
    return new Set(members.filter((m) => assignedIds.has(removeAuth0Prefix(m.id))).map((m) => m.id))
  }, [event.assignedUsers, event.crew, getCrewMemberIds, members])

  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(initialMemberIds)
  const initialMemberIdsKey = useMemo(() => Array.from(initialMemberIds).sort().join('|'), [initialMemberIds])
  const initialSelectionKey = `${event.id ?? 'new'}::${event.crew ?? ''}::${initialMemberIdsKey}`
  const appliedInitialSelectionKeyRef = useRef<string | null>(null)

  useEffect(() => {
    setCurrentEvent(event)
    setCurrentCrew(crewOptions.find((c) => c.value === event.crew) || null)
    setOpenPanel('none')
    // Intentionally omit `baseDialogProps.open` — toggling open when a stacked dialog (e.g. the
    // crew-change confirmation) covers/uncovers us would otherwise reset local state and discard
    // in-progress edits.
  }, [event, event.id, event.crew])

  useEffect(() => {
    if (appliedInitialSelectionKeyRef.current === initialSelectionKey) return

    setSelectedMemberIds(new Set(initialMemberIds))
    appliedInitialSelectionKeyRef.current = initialSelectionKey
  }, [initialMemberIds, initialSelectionKey])

  function toggleMember(id: string) {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const applyCrewChange = (newCrewId: string | null) => {
    setCurrentCrew(crewOptions.find((c) => c.value === newCrewId) || null)
    setCurrentEvent((prev) => ({ ...prev, crew: newCrewId }))
    setSelectedMemberIds(new Set(getCrewMemberIds(newCrewId)))
    setOpenPanel('none')
  }

  const displayMembers = useMemo(() => {
    const selectedCrewId = currentEvent.crew ?? null
    const seen = new Set<string>()
    const result: ScheduleJobMember[] = []
    for (const m of members) {
      if (!seen.has(m.id) && m.crewId === selectedCrewId) {
        seen.add(m.id)
        result.push(m)
      }
    }
    for (const m of members) {
      if (!seen.has(m.id)) {
        seen.add(m.id)
        result.push(m)
      }
    }
    return result
  }, [members, currentEvent.crew])

  const handleClose = () => {
    const { previousProgress: _noCompare, ...initialEventWithoutPreviousProgress } = initialEvent
    const { previousProgress: _noCompare2, ...currentEventWithoutPreviousProgress } = currentEvent
    const dirty = !isEqual(initialEventWithoutPreviousProgress, currentEventWithoutPreviousProgress)

    if (!dirty) {
      dismissAllDialogs()
    } else {
      openDialog(ConfirmationDialog, {
        message: 'You have unsaved changes. If you exit now, your changes will be lost.',
        onConfirm: () => {
          dismissAllDialogs()
        },
        onCancel: () => {
          dismissDialog()
        }
      })
    }
  }

  const rightContent = (
    <IconButton onClick={handleClose} color="inherit" aria-label="Close">
      <ClearIcon />
    </IconButton>
  )

  return (
    <Dialog {...baseDialogProps} classes={{ paper: classes.root }} maxWidth="sm" fullWidth>
      <DialogTitle classes={{ root: classes.dialogTitle }} rightContent={rightContent}>
        <Typography variant="h2" weight={'normal'}>
          {isNew ? 'Schedule' : 'Edit'} Job
        </Typography>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <div className={classes.infoDiv}>
          <Typography variant={'h3'} weight={'normal'}>
            {event.title}
          </Typography>
          <Typography variant={'body2'} color="textSecondary">
            {event.address}
          </Typography>
          <div className={classes.infoDivTextContainer}>
            <Typography variant={'subtitle1'}>{getProjectTypeLabel(event.projectType, options)}</Typography>
            <Typography variant={'subtitle1'} component={'span'}>
              •
            </Typography>
            <Typography variant={'subtitle1'}>Est. {event?.deal?.hours || 0} hours</Typography>
          </div>
        </div>
        {!hideCalendarLink && (
          <Hidden smUp>
            <Button
              variant="dialog"
              color="primary"
              fullWidth
              onClick={() => {
                // Example: pass event ID as a query parameter
                const eventId = currentEvent?.id
                window.location.href = escapeHref(
                  `/scheduling/?tab=calendar${eventId && !isNew ? `&eventId=${encodeURIComponent(eventId)}` : ''}`
                )
              }}
            >
              View Calendar
            </Button>
          </Hidden>
        )}

        <div className={classes.datePicker}>
          <DateRangePicker
            hidePresets
            startDate={currentEvent.start ? moment(currentEvent.start) : null}
            endDate={currentEvent.end ? moment(currentEvent.end) : null}
            onConfirm={(startDate, endDate) => {
              setCurrentEvent((prev) => ({ ...prev, start: startDate?.toDate(), end: endDate?.toDate() }))
            }}
            bothDatesRequired
          />
          {!hideCalendarLink && (
            <Hidden lgUp xsDown>
              <Button
                variant="dialog"
                color="primary"
                fullWidth
                onClick={() => {
                  // Example: pass event ID as a query parameter
                  const eventId = currentEvent?.id
                  window.location.href = escapeHref(
                    `/scheduling/?tab=calendar${eventId && !isNew ? `&eventId=${encodeURIComponent(eventId)}` : ''}`
                  )
                }}
              >
                View Calendar
              </Button>
            </Hidden>
          )}
          {!hideCalendarLink && (
            <Hidden mdDown>
              <Tooltip title="View Calendar" placement="right" icon={<></>} enterDelay={300} enterNextDelay={300}>
                <IconButton
                  onClick={() => {
                    const eventId = currentEvent?.id
                    window.location.href = escapeHref(
                      `/scheduling/?tab=calendar${eventId && !isNew ? `&eventId=${encodeURIComponent(eventId)}` : ''}`
                    )
                  }}
                  color="inherit"
                  className={classes.datePickerIconButton}
                  aria-label="View Calendar"
                >
                  <CalendarTodayIcon />
                </IconButton>
              </Tooltip>
            </Hidden>
          )}
        </div>
        <div>
          <DropdownSelect
            options={crewOptions}
            value={currentCrew}
            label="Crew"
            variant="single"
            placeholder={{ selectPlaceholder: 'Select a crew...' }}
            formatOptionLabel={(option, placement) => {
              const selectedCrew = crews.find((crew) => crew._id === option.value)
              const crewColor = option.crewColor || option.color || selectedCrew?.color
              return (
                <div className={classes.crewOption}>
                  <div className={classes.crewOptionBar} style={{ backgroundColor: crewColor }} />
                  <div className={classes.crewOptionText}>
                    <Typography variant="body1">{option.label}</Typography>
                    {placement === 'menu-item' && option.projectManagerName && (
                      <Typography variant="subtitle1" className={classes.crewOptionSubtext}>
                        Lead: {option.projectManagerName}
                      </Typography>
                    )}
                  </div>
                </div>
              )
            }}
            onChange={(value) => {
              const newCrewId = value?.value ?? null
              const isSameCrew = newCrewId === (currentCrew?.value ?? null)
              console.log({ isSameCrew, newCrewId, currentCrewValue: currentCrew?.value })
              if (isSameCrew) {
                return
              }

              const currentCrewMemberIds = new Set(getCrewMemberIds(currentCrew?.value ?? null))
              const hasCustomSelections = Array.from(selectedMemberIds).some((id) => !currentCrewMemberIds.has(id))

              if (!hasCustomSelections) {
                applyCrewChange(newCrewId)
                return
              }

              openDialog(ConfirmationDialog, {
                title: 'Are you sure?',
                message:
                  'Changing the crew will replace the selected members with the new crew’s defaults. Any custom (unassigned) users or members from other crews you added will be removed.',
                onConfirm: () => {
                  applyCrewChange(newCrewId)
                  dismissDialog()
                },
                onCancel: () => {
                  dismissDialog()
                }
              })
            }}
          />
        </div>
        <div>
          <MemberSelection
            classes={scheduleClasses}
            members={displayMembers}
            selectedMemberIds={selectedMemberIds}
            openPanel={openPanel}
            onOpenPanelChange={setOpenPanel}
            onMemberToggle={toggleMember}
            onMembersClear={() => setSelectedMemberIds(new Set())}
          />
        </div>
        {!isNew && (
          <>
            <div>
              <InputField
                label="Overall Progress"
                type="number"
                fullWidth
                value={currentEvent.progress ? currentEvent.progress : ''}
                placeholder={'0'}
                defaultValue={0}
                onChange={(e) => {
                  const newProgress = parseInt(e.target.value, 10)
                  setCurrentEvent((prev) => ({
                    ...prev,
                    progress: isNaN(newProgress) ? undefined : Math.min(newProgress, 100),
                    previousProgress: prev.progress
                  }))
                }}
                inputProps={{ min: 0, max: 100 }}
              />
              <LinearProgress
                className={classes.linearProgress}
                classes={{ bar: classes.linearProgressBar }}
                value={currentEvent.progress || 0}
                variant="determinate"
              />
              <Typography variant="subtitle2">{currentEvent.progress || 0}% complete</Typography>
            </div>

            <Button
              variant="eventComplete"
              // className={classes.button}
              active={currentEvent.progress === 100}
              fullWidth
              startIcon={<CheckIcon />}
              onClick={() => {
                if (currentEvent.progress === 100) {
                  setCurrentEvent((prev) => ({
                    ...prev,
                    progress: prev.previousProgress || undefined,
                    previousProgress: prev.progress
                  }))
                } else {
                  setCurrentEvent((prev) => ({ ...prev, progress: 100, previousProgress: prev.progress }))
                }
              }}
            >
              {currentEvent.progress === 100 ? 'Job Complete' : 'Mark Job Complete'}
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions
        leftButton={
          !isNew ? (
            <Button
              className={classes.cancel}
              variant={'text'}
              onClick={() => onCancelEvent(currentEvent)}
              disabled={isLoading}
              data-testid="production-event-dialog-cancel-button"
            >
              Delete
            </Button>
          ) : undefined
        }
      >
        <Button
          className={classes.confirm}
          form="production-event-dialog-form"
          variant={'contained'}
          disabled={isLoading || !currentEvent.crew || !currentEvent.start || !currentEvent.end}
          loading={isLoading}
          data-testid="production-event-dialog-confirm-button"
          onClick={handleSubmit}
        >
          {isNew ? 'Schedule Job' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit() {
    setIsLoading(true)
    try {
      const crew = crews.find((c) => c._id === currentEvent.crew)
      const user = users.find((u) => removeAuth0Prefix(u.user_id) === removeAuth0Prefix(crew?.projectManager?.id))

      const allOwners = activeCrews.flatMap((c) =>
        [c.projectManager, c.siteLead, ...(c.members ?? [])].filter((m): m is NonNullable<typeof m> => !!m?.id)
      )
      const assignedUsers =
        selectedMemberIds.size > 0
          ? Object.fromEntries(
              Array.from(selectedMemberIds).map((id) => {
                const owner = allOwners.find((m) => m.id === id)
                const name = owner
                  ? [owner.firstName, owner.lastName].filter(Boolean).join(' ').trim() || undefined
                  : undefined
                return [removeAuth0Prefix(id), { assigned: true as const, ...(name ? { name } : {}) }]
              })
            )
          : undefined

      const structuredEvent = {
        ...currentEvent,
        progress: currentEvent.progress || 0,
        previousProgress: initialEvent.progress || 0, // previousProgress is used to create the activity, so we need to set it back to the previously saved progress
        ...(assignedUsers ? { assignedUsers } : {}),
        estimator:
          currentEvent?.estimator ||
          (user
            ? {
                id: removeAuth0Prefix(user.user_id),
                name: formatName(user.user_metadata),
                email: user.email,
                color: user.user_metadata?.color
              }
            : undefined)
      }
      await onConfirm(structuredEvent)
    } catch (error) {
      Sentry.captureException(error)
      enqueueSnackbar('error', { variant: 'error' })
      setIsLoading(false)
    }
  }
}

export default ProductionEventDialog
